import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";
import SaveButton from "@/components/SaveButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useTranslation } from "react-i18next";
import { useContractorBreadcrumbs } from "../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { postContractor } from "@/services/contractors";
import { WriteContractor } from "@/types/contractor";
import { ApiError } from "@/services/api";
import { PATH_CONTRACTORS } from "@/constants";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useCallback, memo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createContractor } from "@/schemas/contractor";
import { defaultContractorValues } from "../defaultValues";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Input } from "@/components/ui/input";
import GenderSelect from "@/components/GenderSelect";
import DatePicker from "@/components/DatePicker";
import { isRequired } from "@/lib/form";
import { contractor } from "@/schemas/contractor";
import { handlePeselChange } from "../utils/peselUtils";

const ContractorCreator = memo(() => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { can_create } = useModulePermissions();
  const { breadcrumbItems } = useContractorBreadcrumbs();
  const queryClient = useQueryClient();

  const mutation = useMutationWithErrorHandling<
    { id: string },
    ApiError,
    WriteContractor,
    unknown
  >({
    mutationFn: postContractor,
    errorTitle: t("contractors.errors.create"),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["contractors"] });
      navigate(`${PATH_CONTRACTORS}/${response.id}`);
      toast({
        title: t("contractors.success.create"),
      });
    },
  });

  const form = useForm<WriteContractor>({
    resolver: zodResolver(createContractor),
    defaultValues: defaultContractorValues,
    disabled: mutation.isPending || !can_create,
  });

  useFormUnsavedChangesWarning(form);

  const handleSubmit = useCallback(
    async (data: WriteContractor) => {
      await mutation.mutateAsync(data);
    },
    [mutation],
  );

  // Auto-fill birth date and gender from PESEL
  const onPeselFieldsFilled = useCallback(() => {
    toast({
      title: t("contractors.autoFill.peselFilled"),
      duration: 3000,
    });
  }, [toast, t]);

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Form form={form} handleSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t("contractors.createForm.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Field
                name="email"
                label={t("contractors.fields.email")}
                required={isRequired(contractor.shape.email)}
                render={({ field }) => <Input className="w-full" {...field} />}
              />
              <Field
                name="first_name"
                label={t("contractors.fields.first_name")}
                required={isRequired(contractor.shape.first_name)}
                render={({ field }) => <Input className="w-full" {...field} />}
              />
              <Field
                name="last_name"
                label={t("contractors.fields.last_name")}
                required={isRequired(contractor.shape.last_name)}
                render={({ field }) => <Input className="w-full" {...field} />}
              />
              <Field
                name="personal_id_number"
                label={t("contractors.fields.personal_id_number")}
                required={isRequired(contractor.shape.personal_id_number)}
                render={({ field }) => (
                  <Input
                    className="w-full"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handlePeselChange(
                        e.target.value,
                        form,
                        onPeselFieldsFilled,
                      );
                    }}
                  />
                )}
              />
              <Field
                name="birth_date"
                label={t("contractors.fields.birth_date")}
                required={isRequired(contractor.shape.birth_date)}
                render={({ field }) => (
                  <DatePicker className="w-full" {...field} />
                )}
              />
              <Field
                name="gender"
                label={t("contractors.fields.gender")}
                required={isRequired(contractor.shape.gender)}
                render={({ field }) => (
                  <GenderSelect
                    {...field}
                    placeholder={t("contractors.fields.gender")}
                  />
                )}
              />
              <Field
                name="phone_number"
                label={t("contractors.fields.phone_number")}
                required={isRequired(contractor.shape.phone_number)}
                render={({ field }) => <Input className="w-full" {...field} />}
              />
            </Fieldset>
            <div className="flex justify-end mt-4">
              <SaveButton />
            </div>
          </CardContent>
        </Card>
      </Form>
    </>
  );
});

export default ContractorCreator;
